import { LogItem } from "@lhd/types";
import dayjs from "dayjs";
import { LOG_TYPE } from "./contants";

export const parseLoggedAtMs = (loggedAt: string): number => {
  const parsed = dayjs(loggedAt);
  if (parsed.isValid()) return parsed.valueOf();
  const fallback = new Date(loggedAt);
  return Number.isNaN(fallback.getTime()) ? 0 : fallback.getTime();
};

export const parseDisplayTimeMinutes = (
  displayTime?: string | null,
): number | null => {
  const match = displayTime?.trim().match(/^(\d+):([0-5]\d)$/);
  if (!match) return null;

  return Number(match[1]) * 60 + Number(match[2]);
};

export const formatDisplayMinutes = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes < 0) return "00:00";

  const totalMinutes = Math.floor(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(
    2,
    "0",
  )}`;
};

export const getWorkdayEndDisplayMinutes = (
  dayChangeTime?: string | null,
): number => {
  const dayChangeMinutes = parseDisplayTimeMinutes(dayChangeTime);
  if (dayChangeMinutes == null) return 24 * 60;

  return 24 * 60 + (dayChangeMinutes % (24 * 60));
};

export const getDisplayMinutesAt = (
  targetTimeMs: number,
  anchorTimeMs: number,
  anchorDisplayMinutes: number,
): number => {
  const anchorMinuteMs = Math.floor(anchorTimeMs / 60_000) * 60_000;
  return anchorDisplayMinutes + (targetTimeMs - anchorMinuteMs) / 60_000;
};

export const distributeTimelinePercents = (
  rawPercents: number[],
  minimumGapPercent: number,
): number[] => {
  if (rawPercents.length === 0) return [];

  const maximumPossibleGap =
    rawPercents.length > 1 ? 100 / (rawPercents.length - 1) : 0;
  const gap = Math.max(0, Math.min(minimumGapPercent, maximumPossibleGap));
  const percents = rawPercents.map((percent) =>
    Math.min(100, Math.max(0, percent)),
  );

  for (let index = 1; index < percents.length; index++) {
    percents[index] = Math.max(percents[index], percents[index - 1] + gap);
  }

  percents[percents.length - 1] = Math.min(100, percents[percents.length - 1]);
  for (let index = percents.length - 2; index >= 0; index--) {
    percents[index] = Math.max(
      0,
      Math.min(percents[index], percents[index + 1] - gap),
    );
  }

  return percents;
};

const lifecycleTypeIndex = (log: LogItem): number => {
  switch (log.log_type) {
    case LOG_TYPE.CHECKIN:
      return 0;
    case LOG_TYPE.BREAK_START:
      return 1;
    case LOG_TYPE.BREAK_END:
      return 2;
    case LOG_TYPE.CHECKOUT:
      return 3;
    default:
      return 99;
  }
};

export const sortLogsByLoggedAt = (logs: LogItem[]): LogItem[] =>
  [...logs].sort((a, b) => {
    const diff =
      parseLoggedAtMs(a.logged_at || a.created_at) -
      parseLoggedAtMs(b.logged_at || b.created_at);
    if (diff !== 0) return diff;

    const createdDiff =
      parseLoggedAtMs(a.created_at) - parseLoggedAtMs(b.created_at);
    if (createdDiff !== 0) return createdDiff;

    const idA = typeof a.id === "number" ? a.id : 0;
    const idB = typeof b.id === "number" ? b.id : 0;
    if (idA !== idB) return idA - idB;

    return lifecycleTypeIndex(a) - lifecycleTypeIndex(b);
  });

export const getTimedLogs = (logs: LogItem[]): LogItem[] =>
  logs.filter((log) => parseLoggedAtMs(log.logged_at) > 0);

export function hasOpenWorkSession(logs: LogItem[]): boolean {
  const lastLog = sortLogsByLoggedAt(getTimedLogs(logs)).at(-1);
  if (!lastLog) return false;
  return (
    lastLog.log_type === LOG_TYPE.CHECKIN ||
    lastLog.log_type === LOG_TYPE.BREAK_START ||
    lastLog.log_type === LOG_TYPE.BREAK_END
  );
}

const closeOpenWorkingSession = (
  sessionStartMs: number,
  sessionBreakMs: number,
  breakStartMs: number | null,
  endMs: number,
): number => {
  let breakMs = sessionBreakMs;
  if (breakStartMs != null) {
    breakMs += Math.max(0, endMs - breakStartMs);
  }
  return Math.max(0, endMs - sessionStartMs - breakMs);
};

export type LogtimeCalcPrecision = "second" | "minute";

export type CalculateLogtimeMsOptions = {
  precision?: LogtimeCalcPrecision;
};

const toCalcMs = (ms: number, precision: LogtimeCalcPrecision): number => {
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  if (precision === "minute") {
    return Math.floor(ms / 60_000) * 60_000;
  }
  return ms;
};

export function calculateTotalWorkingMs(
  logs: LogItem[],
  nowMs?: number,
  options?: CalculateLogtimeMsOptions,
): number {
  const precision = options?.precision ?? "second";
  const sorted = sortLogsByLoggedAt(logs);
  let totalMs = 0;
  let sessionStartMs: number | null = null;
  let breakStartMs: number | null = null;
  let sessionBreakMs = 0;

  for (const log of sorted) {
    const raw = parseLoggedAtMs(log.logged_at);
    const t = toCalcMs(raw, precision);
    if (!t) continue;

    switch (log.log_type) {
      case LOG_TYPE.CHECKIN:
        if (sessionStartMs != null) {
          totalMs += closeOpenWorkingSession(
            sessionStartMs,
            sessionBreakMs,
            breakStartMs,
            t,
          );
        }
        sessionStartMs = t;
        sessionBreakMs = 0;
        breakStartMs = null;
        break;

      case LOG_TYPE.CHECKOUT:
        if (sessionStartMs != null) {
          totalMs += closeOpenWorkingSession(
            sessionStartMs,
            sessionBreakMs,
            breakStartMs,
            t,
          );
        }
        sessionStartMs = null;
        sessionBreakMs = 0;
        breakStartMs = null;
        break;

      case LOG_TYPE.BREAK_START:
        if (sessionStartMs != null && breakStartMs == null) {
          breakStartMs = t;
        }
        break;

      case LOG_TYPE.BREAK_END:
        if (breakStartMs != null) {
          sessionBreakMs += Math.max(0, t - breakStartMs);
          breakStartMs = null;
        }
        break;
    }
  }

  if (sessionStartMs != null) {
    const endRaw = breakStartMs ?? nowMs;
    const endMs = endRaw != null ? toCalcMs(endRaw, precision) : undefined;
    if (endMs != null && endMs > 0) {
      totalMs += closeOpenWorkingSession(
        sessionStartMs,
        sessionBreakMs,
        breakStartMs,
        endMs,
      );
    }
  }

  return Math.max(0, totalMs);
}

export function calculateTotalBreakMs(
  logs: LogItem[],
  nowMs?: number,
  options?: CalculateLogtimeMsOptions,
): number {
  const precision = options?.precision ?? "second";
  const sorted = sortLogsByLoggedAt(logs);
  let totalMs = 0;
  let breakStartMs: number | null = null;

  for (const log of sorted) {
    const raw = parseLoggedAtMs(log.logged_at);
    const t = toCalcMs(raw, precision);
    if (!t) continue;

    switch (log.log_type) {
      case LOG_TYPE.BREAK_START:
        breakStartMs = t;
        break;

      case LOG_TYPE.BREAK_END:
        if (breakStartMs != null) {
          totalMs += Math.max(0, t - breakStartMs);
          breakStartMs = null;
        }
        break;

      case LOG_TYPE.CHECKOUT:
        if (breakStartMs != null) {
          totalMs += Math.max(0, t - breakStartMs);
          breakStartMs = null;
        }
        break;
    }
  }

  if (breakStartMs != null && nowMs != null) {
    const endMs = toCalcMs(nowMs, precision);
    if (endMs > 0) {
      totalMs += Math.max(0, endMs - breakStartMs);
    }
  }

  return Math.max(0, totalMs);
}

export function calculateOffTime(logs: LogItem[]) {
  let timeOff = 0; // timeoff in miliseconds

  logs.forEach((log, index) => {
    const nextIndex = index + 1;
    const nextLog = logs[nextIndex];
    if (!nextLog) return;

    if (
      log.log_type === LOG_TYPE.BREAK_START &&
      nextLog.log_type === LOG_TYPE.BREAK_END
    ) {
      const start = parseLoggedAtMs(log.logged_at);
      const end = parseLoggedAtMs(nextLog.logged_at);
      timeOff += end - start;
    } else if (
      log.log_type === LOG_TYPE.BREAK_START &&
      nextLog.log_type === LOG_TYPE.CHECKOUT
    ) {
      const start = parseLoggedAtMs(log.logged_at);
      const end = parseLoggedAtMs(nextLog.logged_at);
      timeOff += end - start;
    } else if (
      log.log_type === LOG_TYPE.CHECKOUT &&
      nextLog.log_type === LOG_TYPE.CHECKIN
    ) {
      const start = parseLoggedAtMs(log.logged_at);
      const end = parseLoggedAtMs(nextLog.logged_at);
      timeOff += end - start;
    }
  });
  return timeOff;
}

export function msToHHMMSS(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return `${hh}:${mm}:${ss}`;
}

export const normalizeYmd = (value?: string) => {
  const t = value?.trim();
  return t ? t.replace(/\//g, "-") : "";
};

export const headerName = [
  { title: "", width: "25%" },
  {
    title: "勤務時間",
    width: "20%",
  },
  {
    title: "勤務開始",
    width: "15%",
  },
  {
    title: "",
    width: "5%",
  },
  {
    title: "勤務終了",
    width: "15%",
  },
  {
    title: "",
    width: "20%",
  },
];

export const formatTime = (ms: number) => dayjs(ms).format("HH:mm");

export const formatDuration = (ms: number) => {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = totalMinutes / 60;
  return `${Math.floor(hours)}時間${totalMinutes % 60}分 （${hours.toFixed(
    2,
  )}H ）`;
};

export const getEdgeAlignedStyle = (percent: number): React.CSSProperties => ({
  left: `${percent}%`,
  transform:
    percent <= 2
      ? "translateX(-14px)"
      : percent >= 98
      ? "translateX(-50%)"
      : "translateX(-50%)",
});

export function convertSecondsToHHMM(totalSeconds: number | null): string {
  const seconds = Number(totalSeconds ?? 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return "00:00";

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
